"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.REPORT_COLUMNS = exports.auditLogQuery = exports.catalogReportQuery = exports.catalogReportId = exports.REPORT_ROW_LIMIT = exports.tabularReportResponse = exports.reportRowLink = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
/** نوع الرابط من الصف: يفتح الطرف أو الحساب أو القيد فى شاشته. */
exports.reportRowLink = zod_1.z.object({
    kind: zod_1.z.enum(['customer', 'supplier', 'account']),
    id: zod_1.z.string(),
});
exports.tabularReportResponse = zod_1.z.object({
    companyId: common_1.uuid,
    report: zod_1.z.string(),
    from: common_1.isoDate.nullable(),
    to: common_1.isoDate.nullable(),
    asOf: common_1.isoDate.nullable(),
    rows: zod_1.z.array(zod_1.z.object({
        cells: zod_1.z.record(zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.null()])),
        link: exports.reportRowLink.nullable(),
    })),
    /** مجموع كل عمود مالى على الصفوف الظاهرة. */
    totals: zod_1.z.record(common_1.moneyString),
    /** بلغت الصفوف الحد الأقصى — ضيّق الفترة أو التصفية. */
    truncated: zod_1.z.boolean(),
    /**
     * مطابقة التقرير بمصدر مستقل (الدفتر أو تقرير قائم): `difference` صفر حين يتطابقان،
     * وغير الصفر يُعرض صريحًا للمراجعة لا يُخفى.
     */
    reconciliation: zod_1.z.array(zod_1.z.object({
        label: zod_1.z.string(),
        report: common_1.moneyString,
        reference: common_1.moneyString,
        difference: common_1.moneyString,
    })),
    /**
     * ملاحظات تحت الجدول: ما ينقص لتكتمل الأرقام، أو قرار محاسبى أو ضريبى معلّق
     * (BLOCKED-ACCOUNTING / BLOCKED-TAX)، أو تبويب يحتاج مراجعة.
     */
    notes: zod_1.z.array(zod_1.z.string()),
});
/** حد الصفوف — السجل للقراءة والتصدير، لا لتفريغ القاعدة. */
exports.REPORT_ROW_LIMIT = 2000;
const period = { from: common_1.isoDate, to: common_1.isoDate };
const ordered = (value) => value.from <= value.to;
const orderedIssue = { message: 'from must not be after to', path: ['from'] };
exports.catalogReportId = zod_1.z.enum([
    'sales-invoices',
    'sales-notes',
    'customer-receipts',
    'unapplied-receipts',
    'top-customers',
    'purchase-bills',
    'purchase-notes',
    'supplier-payments',
    'top-suppliers',
    'journal-register',
    'stock-movement',
    'slow-moving',
    'gross-profit-by-item',
    'trial-balance-movement',
    'cash-flow',
    'einvoice-status',
]);
/**
 * معاملات تقارير الفهرس كلها — كل تقرير يقرأ ما يخصه ويُرفض الباقى بلا أثر. `partyId`
 * عميل أو مورد حسب التقرير.
 */
exports.catalogReportQuery = zod_1.z
    .object({
    ...period,
    partyId: common_1.uuid.optional(),
    status: zod_1.z.enum(['all', 'open', 'settled', 'cancelled']).default('all'),
    noteKind: zod_1.z.enum(['all', 'credit', 'debit']).default('all'),
    sourceType: zod_1.z.string().max(64).optional(),
    userId: common_1.uuid.optional(),
    limit: zod_1.z.coerce.number().int().min(1).max(100).default(10),
    itemId: common_1.uuid.optional(),
    /** للأصناف الراكدة: بلا بيع منذ هذا العدد من الأيام حتى `to`. */
    days: zod_1.z.coerce.number().int().min(1).max(3650).default(90),
})
    .refine(ordered, orderedIssue);
exports.auditLogQuery = zod_1.z
    .object({
    ...period,
    userId: common_1.uuid.optional(),
    action: zod_1.z.string().max(100).optional(),
    entityType: zod_1.z.string().max(100).optional(),
})
    .refine(ordered, orderedIssue);
const money = (key, label) => ({ key, label, type: 'money' });
const text = (key, label) => ({ key, label, type: 'text' });
const date = (key, label) => ({ key, label, type: 'date' });
const int = (key, label) => ({ key, label, type: 'int' });
const quantity = (key, label) => ({ key, label, type: 'quantity' });
const percent = (key, label) => ({ key, label, type: 'percent' });
/** أعمدة كل تقرير بترتيب عرضها. */
exports.REPORT_COLUMNS = {
    'sales-invoices': [
        text('number', 'رقم الفاتورة'),
        date('issueDate', 'التاريخ'),
        date('dueDate', 'الاستحقاق'),
        text('party', 'العميل'),
        money('net', 'قبل الضريبة'),
        money('tax', 'الضريبة'),
        money('total', 'الإجمالى'),
        money('creditNotes', 'إشعارات دائنة'),
        money('debitNotes', 'إشعارات مدينة'),
        money('paid', 'المحصّل'),
        money('outstanding', 'المتبقى'),
        text('status', 'الحال'),
    ],
    'sales-notes': [
        text('number', 'رقم الإشعار'),
        date('noteDate', 'التاريخ'),
        text('kind', 'النوع'),
        text('invoice', 'على الفاتورة'),
        text('party', 'العميل'),
        text('reason', 'السبب'),
        money('net', 'قبل الضريبة'),
        money('tax', 'الضريبة'),
        money('total', 'الإجمالى'),
    ],
    'customer-receipts': [
        text('number', 'رقم الإيصال'),
        date('receiptDate', 'التاريخ'),
        text('party', 'العميل'),
        text('account', 'حساب الإيداع'),
        text('reference', 'مرجع'),
        money('amount', 'النقد'),
        money('withholding', 'خصم من المنبع'),
        money('allocated', 'مخصص على فواتير'),
        money('unapplied', 'غير مخصص'),
    ],
    'unapplied-receipts': [
        text('number', 'رقم الإيصال'),
        date('receiptDate', 'التاريخ'),
        text('party', 'العميل'),
        money('settles', 'يسدد (نقد + خصم)'),
        money('allocated', 'المخصص'),
        money('unapplied', 'المتبقى غير المخصص'),
    ],
    'top-customers': [
        int('rank', 'الترتيب'),
        text('party', 'العميل'),
        int('documents', 'الفواتير'),
        money('netSales', 'صافى المبيعات'),
        { key: 'share', label: 'النسبة من المبيعات', type: 'percent' },
        money('outstanding', 'المتبقى عليه'),
    ],
    'purchase-bills': [
        text('number', 'رقم الفاتورة'),
        text('supplierReference', 'رقم المورد'),
        date('billDate', 'التاريخ'),
        date('dueDate', 'الاستحقاق'),
        text('party', 'المورد'),
        money('net', 'قبل الضريبة'),
        money('tax', 'الضريبة'),
        money('total', 'الإجمالى'),
        money('debitNotes', 'إشعارات مدينة'),
        money('creditNotes', 'إشعارات دائنة'),
        money('paid', 'المسدد'),
        money('outstanding', 'المتبقى'),
        text('status', 'الحال'),
    ],
    'purchase-notes': [
        text('number', 'رقم الإشعار'),
        date('noteDate', 'التاريخ'),
        text('kind', 'النوع'),
        text('bill', 'على الفاتورة'),
        text('party', 'المورد'),
        text('reason', 'السبب'),
        money('net', 'قبل الضريبة'),
        money('tax', 'الضريبة'),
        money('total', 'الإجمالى'),
    ],
    'supplier-payments': [
        text('number', 'رقم السداد'),
        date('paymentDate', 'التاريخ'),
        text('party', 'المورد'),
        text('account', 'من حساب'),
        text('reference', 'مرجع'),
        money('amount', 'النقد'),
        money('withholding', 'خصم من المنبع'),
        money('allocated', 'مخصص على فواتير'),
        money('unapplied', 'غير مخصص'),
    ],
    'top-suppliers': [
        int('rank', 'الترتيب'),
        text('party', 'المورد'),
        int('documents', 'الفواتير'),
        money('netPurchases', 'صافى المشتريات'),
        { key: 'share', label: 'النسبة من المشتريات', type: 'percent' },
        money('outstanding', 'المتبقى له'),
    ],
    'journal-register': [
        text('number', 'رقم القيد'),
        date('postingDate', 'التاريخ'),
        text('sourceType', 'المصدر'),
        text('reference', 'المرجع'),
        text('memo', 'البيان'),
        text('status', 'الحال'),
        text('postedBy', 'رحّله'),
        money('amount', 'المبلغ'),
    ],
    'stock-movement': [
        text('item', 'الصنف'),
        text('unit', 'الوحدة'),
        quantity('openingQuantity', 'كمية أول الفترة'),
        money('openingValue', 'قيمة أول الفترة'),
        quantity('inQuantity', 'كمية الوارد'),
        money('inValue', 'قيمة الوارد'),
        quantity('outQuantity', 'كمية الصادر'),
        money('outValue', 'قيمة الصادر'),
        quantity('closingQuantity', 'كمية آخر الفترة'),
        money('closingValue', 'قيمة آخر الفترة'),
    ],
    'slow-moving': [
        text('item', 'الصنف'),
        text('unit', 'الوحدة'),
        quantity('quantity', 'الكمية'),
        money('value', 'القيمة'),
        date('lastSale', 'آخر بيع'),
        date('lastMovement', 'آخر حركة'),
        int('idleDays', 'أيام بلا بيع'),
    ],
    'gross-profit-by-item': [
        text('item', 'الصنف'),
        text('kind', 'النوع'),
        money('netSales', 'صافى المبيعات'),
        money('cost', 'التكلفة'),
        money('grossProfit', 'مجمل الربح'),
        percent('margin', 'هامش الربح'),
    ],
    'trial-balance-movement': [
        text('account', 'الحساب'),
        money('openingDebit', 'أول الفترة مدين'),
        money('openingCredit', 'أول الفترة دائن'),
        money('periodDebit', 'حركة مدين'),
        money('periodCredit', 'حركة دائن'),
        money('closingDebit', 'آخر الفترة مدين'),
        money('closingCredit', 'آخر الفترة دائن'),
    ],
    'cash-flow': [
        text('section', 'النشاط'),
        text('line', 'البند'),
        money('amount', 'المبلغ'),
        money('sectionTotal', 'صافى النشاط'),
    ],
    'einvoice-status': [
        text('number', 'رقم الفاتورة'),
        date('issueDate', 'التاريخ'),
        text('party', 'العميل'),
        money('total', 'الإجمالى'),
        text('readiness', 'الجاهزية'),
        text('issues', 'ما ينقصها'),
        text('submission', 'الإرسال للمنظومة'),
    ],
    'audit-log': [
        text('at', 'الوقت'),
        text('actor', 'المستخدم'),
        text('action', 'الإجراء'),
        text('entityType', 'نوع السجل'),
        text('entityId', 'المعرّف'),
        text('requestId', 'رقم الطلب'),
    ],
};
//# sourceMappingURL=report-catalog.js.map